/**
 * shareCard/codec — pack a ShareCard into one URL path segment, and back.
 *
 * Format:  "1." + base64url(JSON)            plain
 *          "1z." + base64url(gzip(JSON))     gzipped
 * base64url is unpadded, so the whole string is [A-Za-z0-9._-] — safe in a
 * path segment with no further escaping. decode accepts either prefix.
 *
 * WHICH FORM: whichever is SHORTER, plain on a tie. This used to be a size
 * threshold (gzip only past 1500 JSON bytes), which made the link length
 * non-monotonic: a 20-length set card sat just under it and went out as 1,886
 * characters of plain base64, while the SAME set at 22 lengths crossed it and
 * went out gzipped at 504. iOS Messages draws a link that long as a wall of
 * text with no preview — "this massive random string in front of the image"
 * (Scott, 2026-09-12). Encoding both ways costs microseconds and can never
 * lose. Old links keep opening: decode reads either prefix, and nothing about
 * the two wire forms changed.
 *
 * INTEGRITY, stated plainly: "1z." carries gzip's own CRC32, so a corrupted
 * gzipped card is detected. "1." carries NOTHING — flipping one character
 * inside a JSON string value yields a different but still-valid card. That is
 * accepted: a client-side checksum would catch accidental corruption but is
 * trivially recomputed, so it would advertise tamper-resistance the format
 * cannot deliver. Treat a decoded card as UNTRUSTED INPUT, never as proof of
 * what someone swam. If these links ever need to be authentic, that is a
 * server-side signature and a version bump, not a checksum here.
 *
 * ASYNC, unavoidably. The task asked for a synchronous `(card) => string`, but
 * gzip in a browser exists ONLY as CompressionStream, which is a stream and
 * therefore async; there is no sync gzip on the web platform at all. Rather
 * than fake it, both functions return a Promise. Callers: `await`.
 *
 * Node fallback: when CompressionStream is absent we reach for node:zlib
 * through a NON-LITERAL specifier so Vite/Rollup cannot statically follow it
 * and the browser bundle never pulls zlib.
 */
import { isShareCard, type ShareCard } from "./types";

/** TS distinguishes Uint8Array<ArrayBuffer> from the ArrayBufferLike-backed
 *  default; the Web stream writers accept only the former. One alias keeps
 *  every byte hop in this file on the same side of that line. */
type Bytes = Uint8Array<ArrayBuffer>;

const PLAIN_PREFIX = "1.";
const GZIP_PREFIX = "1z.";
/** Fable verify L3: the most a gzipped card may inflate to. The encoded input is capped at 8 KB (routes.ts),
 *  so a real card's JSON is well under this; a gzip bomb that fits the input cap is refused once the
 *  inflated total passes it, before it is copied, decoded or parsed. Not bounded: the native transform
 *  still inflates the whole 8 KB input into its queue first (up to ~6 MB; measured, slicing the writes
 *  does not change it), so peak memory per request stays about 6 MB (Fable r1, hotfix3). */
const MAX_DECODED_BYTES = 256 * 1024;

function toBase64Url(bytes: Bytes): string {
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function fromBase64Url(s: string): Bytes {
  if (!/^[A-Za-z0-9_-]+$/.test(s)) throw new Error("share card: not base64url");
  const b64 = s.replace(/-/g, "+").replace(/_/g, "/");
  const bin = atob(b64 + "=".repeat((4 - (b64.length % 4)) % 4));
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/** The shape CompressionStream / DecompressionStream actually expose (their
 *  writable takes BufferSource, not Uint8Array — so TransformStream<Uint8Array,
 *  Uint8Array> does not fit). */
interface ByteTransform {
  readonly writable: WritableStream<BufferSource>;
  readonly readable: ReadableStream<Bytes>;
}

async function pump(t: ByteTransform, bytes: Bytes, limit = Infinity): Promise<Bytes> {
  const writer = t.writable.getWriter();
  // The read loop below surfaces any transform error; swallow the mirrored
  // rejection here so a corrupt payload is one thrown error, not also an
  // unhandled rejection that fails the whole test run.
  void writer
    .write(bytes)
    .then(() => writer.close())
    .catch(() => {});
  const reader = t.readable.getReader();
  const chunks: Uint8Array[] = [];
  let total = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    total += value.length;
    if (total > limit) {
      void reader.cancel().catch(() => {});
      throw new Error("inflated past the limit");
    }
  }
  const out = new Uint8Array(total);
  let at = 0;
  for (const c of chunks) {
    out.set(c, at);
    at += c.length;
  }
  return out;
}

/** node:zlib, loaded only when the Web streams are missing. */
async function nodeZlib(): Promise<{ gzipSync: (b: Bytes) => Bytes; gunzipSync: (b: Bytes, o?: { maxOutputLength: number }) => Bytes }> {
  const specifier = "node:" + "zlib";
  return (await import(/* @vite-ignore */ specifier)) as never;
}

async function gzip(bytes: Bytes): Promise<Bytes> {
  if (typeof CompressionStream !== "undefined") return pump(new CompressionStream("gzip"), bytes);
  return new Uint8Array((await nodeZlib()).gzipSync(bytes));
}

async function gunzip(bytes: Bytes): Promise<Bytes> {
  if (typeof DecompressionStream !== "undefined") return pump(new DecompressionStream("gzip"), bytes, MAX_DECODED_BYTES);
  return new Uint8Array((await nodeZlib()).gunzipSync(bytes, { maxOutputLength: MAX_DECODED_BYTES }));
}

export async function encodeShareCard(card: ShareCard): Promise<string> {
  const json = new TextEncoder().encode(JSON.stringify(card));
  const plain = PLAIN_PREFIX + toBase64Url(json);
  const gzipped = GZIP_PREFIX + toBase64Url(await gzip(json));
  return gzipped.length < plain.length ? gzipped : plain;
}

/** Throws on a bad prefix, non-base64url, a corrupt payload, non-JSON, or a
 *  card this build cannot read (a set card other than v1, a swim card other
 *  than v1/v2). See the INTEGRITY note above for what it CANNOT catch. */
export async function decodeShareCard(s: string): Promise<ShareCard> {
  const gzipped = s.startsWith(GZIP_PREFIX);
  if (!gzipped && !s.startsWith(PLAIN_PREFIX)) throw new Error("share card: unknown prefix");
  const body = s.slice(gzipped ? GZIP_PREFIX.length : PLAIN_PREFIX.length);
  let json: string;
  try {
    const raw = fromBase64Url(body);
    json = new TextDecoder().decode(gzipped ? await gunzip(raw) : raw);
  } catch (cause) {
    throw new Error("share card: corrupt payload", { cause });
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch (cause) {
    throw new Error("share card: not JSON", { cause });
  }
  if (!isShareCard(parsed)) throw new Error("share card: not a v1 card");
  return parsed;
}
